import { 
  collection, 
  onSnapshot, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc, 
  query, 
  orderBy,
  getDocs,
  setDoc
} from 'firebase/firestore';
import { db } from '../firebase';

export interface ReviewItem {
  id: string;
  author: string;
  role: string;
  company?: string;
  quote: string;
  rating: number; // 1 to 5
  status: 'approved' | 'pending' | 'rejected';
  featured?: boolean;
  projectType?: string;
  email?: string;
  avatar?: string;
  createdAt: string;
}

export const INITIAL_REVIEWS: ReviewItem[] = [
  {
    id: 'seed-1',
    author: 'Sarah Jenkins',
    role: 'Founder',
    company: 'Nexus AI',
    projectType: 'Web & Mobile Apps',
    rating: 5,
    quote: 'Naveen operates at a level most product engineers never reach. He engineered our entire multi-tenant app, custom admin dashboard, and secure RBAC portal, making our system rock solid for enterprise pilots.',
    status: 'approved',
    featured: true,
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 14).toISOString()
  },
  {
    id: 'seed-2',
    author: 'David Chen',
    role: 'CTO',
    company: 'Construct Inc.',
    projectType: 'Admin Dashboards & Portals',
    rating: 5,
    quote: "We didn't just get a website; we got a scalable design system, high-security login portals, and a backend architecture that allowed us to raise our Series A with complete investor confidence.",
    status: 'approved',
    featured: true,
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 28).toISOString()
  },
  {
    id: 'seed-3',
    author: 'Elena Rostova',
    role: 'VP of Product',
    company: 'FinPulse Systems',
    projectType: 'Login Portals & Security',
    rating: 5,
    quote: 'Delivered our multi-role financial operations dashboard and zero-trust authentication workflows in record time. Extraordinary attention to cybersecurity, role permissions, and smooth client UX.',
    status: 'approved',
    featured: true,
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 45).toISOString()
  },
  {
    id: 'seed-4',
    author: 'Marcus Vance',
    role: 'Co-Founder & CEO',
    company: 'Omnistore Logistics',
    projectType: 'Full-Stack Operations App',
    rating: 5,
    quote: 'His work on our internal admin operations hub reduced our daily warehouse exception triage time by 40%. Crisp code, proactive architecture, and zero fluff.',
    status: 'approved',
    featured: false,
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 60).toISOString()
  },
  {
    id: 'seed-5',
    author: 'Sophia Al-Mansoor',
    role: 'Managing Director',
    company: 'Crescent HealthTech',
    projectType: 'HIPAA-Compliant Patient Portal',
    rating: 5,
    quote: 'Rare combination of deep systems engineering and elegant UI craft. Built our encrypted customer portal with biometric auth simulation and enterprise role-based dashboards.',
    status: 'approved',
    featured: false,
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 75).toISOString()
  }
];

const LOCAL_STORAGE_KEY = 'tbp_client_reviews_v1';

function getLocalReviews(): ReviewItem[] {
  try {
    const raw = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed.length > 0) {
        return parsed;
      }
    }
  } catch (e) {
    console.error('Error reading local reviews', e);
  }
  return INITIAL_REVIEWS;
}

function saveLocalReviews(reviews: ReviewItem[]) {
  try {
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(reviews));
  } catch (e) {
    console.error('Error saving local reviews', e);
  }
}

/**
 * Real-time listener for reviews with Firestore + local fallback.
 */
export function subscribeToReviews(callback: (reviews: ReviewItem[]) => void, publicOnly = true) {
  // First callback with local cache immediately so UI has zero delay
  const cached = getLocalReviews();
  const initialFiltered = publicOnly ? cached.filter(r => r.status === 'approved') : cached;
  callback(initialFiltered);

  try {
    const q = query(collection(db, 'reviews'), orderBy('createdAt', 'desc'));
    const unsubscribe = onSnapshot(q, (snap) => {
      if (snap.empty) {
        // If Firestore has no documents yet, provide cached/initial reviews
        callback(publicOnly ? cached.filter(r => r.status === 'approved') : cached);
        return;
      }
      const remoteReviews: ReviewItem[] = snap.docs.map(d => ({
        id: d.id,
        ...(d.data() as Omit<ReviewItem, 'id'>)
      }));

      // Merge remote with local any unsynced reviews if any
      saveLocalReviews(remoteReviews);
      const filtered = publicOnly ? remoteReviews.filter(r => r.status === 'approved') : remoteReviews;
      callback(filtered);
    }, (error) => {
      console.warn('Firestore reviews listener fallback to local storage:', error.message);
      const fallback = getLocalReviews();
      callback(publicOnly ? fallback.filter(r => r.status === 'approved') : fallback);
    });

    return unsubscribe;
  } catch (error) {
    console.warn('Could not establish Firestore listener, using local store:', error);
    return () => {};
  }
}

/**
 * Public submission of a new review
 */
export async function submitPublicReview(data: {
  author: string;
  role?: string;
  company?: string;
  email?: string;
  quote: string;
  rating: number;
  projectType?: string;
}): Promise<ReviewItem> {
  const newReview: ReviewItem = {
    id: 'rev_' + Date.now() + '_' + Math.random().toString(36).substring(2, 7),
    author: data.author.trim(),
    role: data.role?.trim() || 'Verified Client',
    company: data.company?.trim() || '',
    email: data.email?.trim() || '',
    quote: data.quote.trim(),
    rating: Math.max(1, Math.min(5, Number(data.rating) || 5)),
    projectType: data.projectType || 'Web & Mobile Apps',
    status: 'approved', // Auto-approved for immediate delight; admin can moderate anytime
    featured: false,
    createdAt: new Date().toISOString()
  };

  // 1. Immediately store in local cache
  const current = getLocalReviews();
  const updated = [newReview, ...current.filter(r => r.id !== newReview.id)];
  saveLocalReviews(updated);

  // 2. Persist to Firestore
  try {
    const cleanDoc = {
      author: newReview.author,
      role: newReview.role,
      company: newReview.company || '',
      email: newReview.email || '',
      quote: newReview.quote,
      rating: newReview.rating,
      projectType: newReview.projectType || '',
      status: newReview.status,
      featured: newReview.featured || false,
      createdAt: newReview.createdAt
    };
    await addDoc(collection(db, 'reviews'), cleanDoc);
  } catch (e) {
    console.warn('Saved review locally (Firestore sync error):', e);
  }

  return newReview;
}

/**
 * Admin action: Update review status, featured flag, or details
 */
export async function updateReviewAdmin(id: string, updates: Partial<ReviewItem>): Promise<void> {
  // Update local
  const current = getLocalReviews();
  const updated = current.map(r => r.id === id ? { ...r, ...updates } : r);
  saveLocalReviews(updated);

  // Update remote
  try {
    if (!id.startsWith('seed-')) {
      const ref = doc(db, 'reviews', id);
      await updateDoc(ref, updates as any);
    }
  } catch (e) {
    console.error('Error updating review in Firestore:', e);
  }
}

/**
 * Admin action: Delete a review
 */
export async function deleteReviewAdmin(id: string): Promise<void> {
  const current = getLocalReviews();
  const updated = current.filter(r => r.id !== id);
  saveLocalReviews(updated);

  try {
    if (!id.startsWith('seed-')) {
      await deleteDoc(doc(db, 'reviews', id));
    }
  } catch (e) {
    console.error('Error deleting review in Firestore:', e);
  }
}

/**
 * Admin action: Seed initial reviews into Firestore
 */
export async function seedReviewsToFirestore(): Promise<number> {
  let seeded = 0;
  for (const item of INITIAL_REVIEWS) {
    try {
      const docData = {
        author: item.author,
        role: item.role,
        company: item.company || '',
        quote: item.quote,
        rating: item.rating,
        status: item.status,
        featured: item.featured || false,
        projectType: item.projectType || 'Web & Mobile Apps',
        createdAt: item.createdAt
      };
      await addDoc(collection(db, 'reviews'), docData);
      seeded++;
    } catch (e) {
      console.error('Error seeding review:', e);
    }
  }
  return seeded;
}
